import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import PropTypes from 'prop-types';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import Heading from '../Onboarding/components/Heading';
import { DashboardWrapper, SpinnerHolder } from '../common/styles';
import DownloadComponent from '../common/components/DownloadComponent/DownloadComponent';
import { UploadsContainer } from './styles';
import { ConfirmUpload } from '../Onboarding/components/styles';
import usePolling from '../common/utils/usePolling';
import {
  employeeUploadMsg,
  genericErrorMsg,
  uploadSuccessStatus,
} from '../common/components/constants';
import ErrorPage from '../common/components/ErrorPage';

const UploadStatus = ({
  isFetching,
  uploadMsg,
  clearUpload,
  fetched,
  getUploadStatus,
  uploadStatusData,
  setFileUploadMsg,
}) => {
  const history = useHistory();
  const { id: processId = '' } = useParams();
  const { uploads = [], status = '' } = uploadStatusData;
  const [uploadStatus, setUploadStatus] = useState('');
  const [isPollingStarted, setIsPollingStarted] = useState(false);
  const [pollingFrequency, setPollingFrequency] = useState(2);

  history.listen(location => {
    if (location.pathname) {
      clearUpload();
    }
  });

  useEffect(() => {
    getUploadStatus(processId);
  }, []);

  useEffect(() => {
    if (uploadStatus === 'completed') {
      setIsPollingStarted(false);
    }
  }, [uploadStatus]);

  useEffect(() => {
    setUploadStatus(status);
    if (status === 'started' || status === 'processing') {
      setIsPollingStarted(true);
    }
  }, [status]);

  const onResponse = ({ isError, response }) => {
    let message = '';
    const currentStatus = _get(response, 'data.status', '');
    setUploadStatus(currentStatus);
    if (uploadSuccessStatus.includes(uploadStatus)) {
      message = _get(response, 'data.message.title', employeeUploadMsg);
    } else {
      message = _get(response, 'data.message.title', genericErrorMsg);
    }

    if (isError) {
      setIsPollingStarted(false);
      setFileUploadMsg(employeeUploadMsg);
      return;
    }
    setFileUploadMsg(message);
    if (uploadSuccessStatus.includes(uploadStatus) && !isError) {
      setPollingFrequency(3);
    } else {
      setIsPollingStarted(false);
    }
  };

  usePolling({
    processId,
    frequency: pollingFrequency,
    isPollingStarted,
    onResponse,
  });

  if (!fetched) {
    return (
      <DashboardWrapper>
        <ErrorPage />
      </DashboardWrapper>
    );
  }

  return isFetching ? (
    <DashboardWrapper>
      <SpinnerHolder className='center'>
        <Spinner color={red.z_red} size='medium' />
      </SpinnerHolder>
    </DashboardWrapper>
  ) : (
    <DashboardWrapper>
      <Heading
        heading='Employee Data Upload Report'
        subheading='You can check data upload report from below'
      />
      <UploadsContainer>
        {uploadStatus === 'completed' && !isPollingStarted ? (
          <>
            {uploads.map(uploadInfo => {
              const { title = '', subtitle = '', link = '' } = uploadInfo;
              return (
                <DownloadComponent title={title} label={subtitle} link={link} />
              );
            })}
          </>
        ) : (
          <ConfirmUpload
            status={uploadStatus}
            progress={isPollingStarted}
            show={uploadStatus !== 'completed'}
          >
            {uploadMsg}
          </ConfirmUpload>
        )}
      </UploadsContainer>
    </DashboardWrapper>
  );
};

UploadStatus.propTypes = {
  isFetching: PropTypes.bool,
  fetched: PropTypes.bool,
  uploadMsg: PropTypes.string,
  clearUpload: PropTypes.func,
  uploadStatusData: PropTypes.objectOf(PropTypes.any),
  getUploadStatus: PropTypes.func,
  setFileUploadMsg: PropTypes.func,
};

UploadStatus.defaultProps = {
  isFetching: true,
  uploadMsg: '',
  clearUpload: _noop,
  fetched: true,
  uploadStatusData: {},
  setFileUploadMsg: _noop,
  getUploadStatus: _noop,
};

export default UploadStatus;
